<template>
  <div
    :class="[darkMode ? 'dark' : '']"
    class="w-full min-h-screen flex flex-col"
  >
    <Header />
    <main
      class="
        w-full
        px-6
        pb-16
        box-border
        bg-mainBackground-light
        dark:bg-mainBackground-dark
        flex
        justify-center
        flex-grow
        transition-all
        duration-300
      "
    >
      <article
        class="
          w-full
          max-w-sm
          sm:max-w-lgr
          mx-auto
          -mt-38
          sm:-mt-56
          2xl:-mt-48
          bg-transparent
        "
      >
        <div class="mb-7 sm:mb-8 flex items-center">
          <h1
            class="
              text-2xl
              sm:text-3xl
              font-bold font-josefin
              text-white
              tracking-ultra
            "
          >
            TODO
          </h1>
          <button
            @click="changeTheme"
            class="
              w-5
              h-5
              mb-1
              sm:w-6
              sm:h-6
              ml-auto
              bg-moon
              dark:bg-sun
              bg-cover bg-no-repeat bg-center
            "
          >
            <span class="sr-only">Theme switcher</span>
          </button>
        </div>
        <NewTask @add-task="addNewTask" />
        <div
          class="
            mt-4
            sm:mt-6
            rounded
            bg-todoBackground-light
            dark:bg-todoBackground-dark
            flex flex-col
            shadow-todoShadowLight
            dark:shadow-todoShadowDark
          "
        >
          <Tasks
            :tasks="tasks"
            @delete-task="deleteTask"
            @change-status="updateTaskStatus"
            @save-new-task-order="loopTroughtNewList"
          />
          <div
            class="
              relative
              sm:static
              px-4
              sm:px-5
              py-3
              flex flex-row
              items-center
            "
          >
            <Counter :uncompletedTasks="uncompletedTasks" />
            <Filters
              :activeFilter="activeFilter"
              @activate-filter="changeActivFilter"
            />
            <Cleaner @delete-completed="cleanTaskList" />
          </div>
        </div>
        <p
          class="
            mt-26
            sm:mt-12
            text-xs
            sm:text-sm
            font-bold font-josefin
            text-noteText-light
            dark:text-noteText-dark
            text-center
          "
        >
          Drag and drop to reorder list
        </p>
      </article>
    </main>
    <Footer />
  </div>
</template>
<script>
import Tasks from "./components/Tasks";
import NewTask from "./components/NewTask";
import Footer from "./components/Footer";
import Counter from "./components/Counter";
import Filters from "./components/Filters";
import Cleaner from "./components/Cleaner";
import Header from "./components/Header";

export default {
  name: "App",
  components: {
    Tasks,
    NewTask,
    Footer,
    Counter,
    Filters,
    Cleaner,
    Header,
  },
  data() {
    return {
      tasks: [],
      allTasks: [],
      uncompletedTasks: [],
      completedTasks: [],
      darkMode: false,
      activeFilter: "all",
    };
  },
  methods: {
    // Redefining the activeFilter and tasks variables
    async changeActivFilter(parameter) {
      if (parameter === "all") {
        this.activeFilter = "all";
        this.tasks = await this.filterTasks();
      }
      if (parameter === "uncompleted") {
        this.activeFilter = "uncompleted";
        this.tasks = await this.filterTasks();
      }
      if (parameter === "completed") {
        this.activeFilter = "completed";
        this.tasks = await this.filterTasks();
      }
    },
    // Changing the theme value in local storage and redefining the darkMode variable
    changeTheme() {
      if (!this.darkMode) {
        localStorage.theme = "dark";
        this.darkMode = this.setTheme();
      } else {
        localStorage.theme = "light";
        this.darkMode = this.setTheme();
      }
    },
    /* Check OS preference and if dark mode is on remove theme item
    from the local storage */
    checkOSThemePreference() {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        localStorage.removeItem("theme");
        this.darkMode = this.setTheme();
      }
    },
    // Set theme logic
    setTheme() {
      /* If theme value in local storage is set to dark or 
      there is no theme item in local storage and prefered 
      OS theme is dark return true, else return false */
      if (
        localStorage.theme === "dark" ||
        (!("theme" in localStorage) &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      ) {
        return true;
      }
      return false;
    },
    /* Go through the list of completed tasks and request 
    their removal from the database */
    cleanTaskList() {
      this.completedTasks.forEach((task) => {
        this.deleteTask(task.id);
      });
    },
    // Remove the task with the specified id from the database
    async deleteTask(taskId) {
      await fetch(`https://json-todo-api.herokuapp.com/tasks/${taskId}`, {
        method: "DELETE",
      });
      this.tasks = await this.filterTasks();
    },
    // Update the status of the specified task
    async updateTaskStatus(taskId) {
      const target = await this.fetchTask(taskId);
      const updateTask = { ...target, done: !target.done };
      const response = await fetch(
        `https://json-todo-api.herokuapp.com/tasks/${taskId}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json",
          },
          body: JSON.stringify(updateTask),
        }
      );
      this.tasks = await this.filterTasks();
    },
    async loopTroughtNewList(newTaskList) {
      newTaskList.forEach((task) => {
        this.saveNewTaskOrder(task.id, task.done, task.description);
      });
    },
    async saveNewTaskOrder(taskID, taskDone, taskDescription) {
      const target = await this.fetchTask(taskID);
      const updateTask = {
        ...target,
        done: taskDone,
        description: taskDescription,
      };
      const response = await fetch(
        `https://json-todo-api.herokuapp.com/tasks/${taskID}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json",
          },
          body: JSON.stringify(updateTask),
        }
      );
    },
    // Add the new task to the database
    async addNewTask(newTask) {
      await fetch("https://json-todo-api.herokuapp.com/tasks", {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify(newTask),
      });
      this.tasks = await this.filterTasks();
    },
    // Set the tasks variable based on the activeFilter value
    async filterTasks() {
      this.allTasks = await this.fetchTasks();
      this.uncompletedTasks = await this.checkStatus(false);
      this.completedTasks = await this.checkStatus(true);
      if (this.activeFilter === "all") {
        return this.allTasks;
      }
      if (this.activeFilter === "uncompleted") {
        return this.uncompletedTasks;
      }
      if (this.activeFilter === "completed") {
        return this.completedTasks;
      }
      return;
    },
    // Fetch and return all tasks
    async fetchTasks() {
      const response = await fetch("https://json-todo-api.herokuapp.com/tasks");
      const jsonData = await response.json();
      return jsonData;
    },
    // Fetch and return the task with the specifed id
    async fetchTask(taskId) {
      const response = await fetch(
        `https://json-todo-api.herokuapp.com/tasks/${taskId}`
      );
      const jsonData = await response.json();
      return jsonData;
    },
    // Fetch all tasks, run filter and return tasks with the specifed status
    async checkStatus(status) {
      const tasks = await this.fetchTasks();
      return tasks.filter((task) => task.done === status);
    },
  },
  // When the instance is created redefine variables
  async created() {
    this.tasks = await this.filterTasks();
    this.darkMode = this.setTheme();
    this.checkOSThemePreference();
  },
};
</script>
<style>
@import url("https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@400;700&display=swap");
</style>